import { z } from "zod";

import {
  checkboxGroupEnabledReminder,
  noParallelToolCallsReminder,
  selectEnabledActionReminder,
  selectEnabledOptionReminder,
} from "./operatorToolReminders";

/**
 * The model-visible contract - description and parameter schema - for Operator tool names
 * that more than one registration site declares.
 *
 * Every registration sharing a name through `toolEntityId` collapses into the single
 * definition sent to the model, so they must all supply the same description and the same
 * schema. Two sites can share a name without either knowing about the other: a `Braze*`
 * wrapper rendered inside an editor iframe registers through the bridge as well as on the
 * host page, and the bridge cannot carry a schema's parameter prose across, since a bridged
 * schema would otherwise be a channel for iframe-authored text into the model's context. The
 * registry names this module's schema as the canonical one and discards the wire schema, so
 * both paths end up with the same definition.
 *
 * A control's own detail belongs in its `operatorLabel`, which reaches the model through the
 * scraped page and the tool result, not in the shared description.
 */

/* --------------------------------- Buttons --------------------------------- */

/** Shared by every `useClickTool` caller registering `click_button`. */
export const clickButtonDescription =
  "Activate the matching button on the current page. Identify the target from read_current_page.";

/** Shared by every `useClickTool` caller registering `click_icon_button`. */
export const clickIconButtonDescription =
  "Activate the matching icon button on the current page. Identify the target from read_current_page.";

/** Shared by every `useClickTool` caller registering `click_close_button`. */
export const clickCloseButtonDescription =
  "Close the matching dialog or panel via its close control. Identify the target from read_current_page.";

/**
 * Shared by `useRegisterRowActionClickTool`. The registry allowlists the
 * gallery's `useTemplate` name so those registrations can cross editor iframes.
 */
export const clickTableRowActionDescription =
  "Click a table row action. Match the target button in read_current_page by toolEntityId.";

/* ---------------------------------- Menus ---------------------------------- */

/**
 * Shared by every `useMenuActionTool` caller registering `click_menu_action`. Every menu
 * registers this one name, so the contract stays static: naming a menu's own actions here
 * would describe one instance to the model and misdescribe the rest. `toolEntityId` is what
 * scopes the call to a menu.
 */
export const clickMenuActionDescription =
  "Activate a menu action from the matching menu on the current page. Identify valid actions from read_current_page before calling." +
  selectEnabledActionReminder;

export const clickMenuActionParameters = z.object({
  actionLabel: z
    .string()
    .describe(
      'The label of an enabled menu action to click. Must correspond to the "value" attribute of a non-disabled <option> in the target menu <select> from read_current_page. You MUST read the page content before using this tool to select a valid action.'
    ),
});

/* ------------------------------- Text inputs ------------------------------- */

/** Shared by `BrazeInput`, `BrazeLiquidInput` and `BrazeInlineInput`. */
export const updateInputDescription =
  "Set the value of the matching text input on the current page. Identify the target from read_current_page.";

export const updateInputParameters = z.object({
  text: z.string().describe("The text to set as the input value"),
});

/** Shared by `BrazeTextarea` and `BrazeLiquidTextarea`. */
export const updateTextareaDescription =
  "Set the value of the matching textarea on the current page. Identify the target from read_current_page.";

export const updateTextareaParameters = z.object({
  text: z.string().describe("The text to set as the textarea value"),
});

export const updateSearchInputDescription =
  "Set the value of the matching search input on the current page. Identify the target from read_current_page.";

export const updateSearchInputParameters = z.object({
  text: z.string().describe("The text to set as the search input value"),
});

export const updateNumberInputDescription =
  "Set the numeric value of the matching number input on the current page. Identify the target from read_current_page.";

export const updateNumberInputParameters = z.object({
  value: z
    .number()
    .describe(
      "The number to set as the input value. If min, max, or step are provided, this value must be within the defined range."
    ),
});
export const updateColorPickerDescription =
  "Set the color of the matching color picker on the current page. Identify the target from read_current_page.";
export const updateColorPickerParameters = z.object({
  color: z.string().describe("The hex color value (e.g., #FF5733 or #FF5733AA with alpha)"),
});

/** Every Monaco on the page: the sidebar's HTML control and its enlarge modal are both one. */
export const updateEditorDescription =
  "Set the contents of the matching code editor on the current page. Identify the target from read_current_page.";

export const updateEditorParameters = z.object({
  text: z.string().describe("The text to set as the editor value"),
});

/* -------------------------------- Tag inputs -------------------------------- */

/** Shared by `BrazeTagInput`. */
export const setTagInputDescription =
  "Set values in the matching tag input on the current page, replacing the full list. Identify the target from read_current_page.";

export const setTagInputParameters = z.object({
  values: z.array(z.string()).describe("List of values to set. Provide an empty array to clear all values."),
});

/* --------------------------------- Selects --------------------------------- */

export const selectOptionDescription =
  "Set a dropdown to one option. Use the lookup_options tool first when options load asynchronously." +
  selectEnabledOptionReminder;

export const selectOptionParameters = z.object({
  optionValue: z
    .string()
    .describe(
      "The label or value of the option to select. For async dropdowns, use lookup_options first and pass a label it returned. For static dropdowns, match an option from read_current_page."
    ),
});

export const selectOptionsDescription =
  "Set a multi-select dropdown to one or more options. Use the lookup_options tool first when options load asynchronously." +
  selectEnabledOptionReminder;

export const selectOptionsParameters = z.object({
  optionValues: z
    .array(z.string())
    .describe(
      "Array of option labels or values to select. For async dropdowns, each value must be returned by the lookup_options tool. For static dropdowns, match options from read_current_page."
    ),
});

/** `BrazeSelect`'s `useSearchTool`, which every async select variant mounts. */
export const lookupOptionsDescription =
  "Search for options in a dropdown before selecting. Use when the dropdown loads options asynchronously or the full list is not visible in read_current_page.";

export const lookupOptionsParameters = z.object({
  searchQuery: z
    .string()
    .describe(
      "The search query to find matching options. Use keywords from the option name you're looking for. An empty string returns all available options."
    ),
});

export const selectOptionAsyncDescription =
  "Set an async dropdown to one option. Use the lookup_options tool first to find available options." +
  selectEnabledOptionReminder;

export const selectOptionAsyncParameters = z.object({
  optionValue: z
    .string()
    .describe("The label of the option to select. Must be one of the options returned by the lookup_options tool."),
});

export const selectOptionsAsyncDescription =
  "Set an async multi-select dropdown to one or more options. Use the lookup_options tool first to find available options." +
  selectEnabledOptionReminder;

export const selectOptionsAsyncParameters = z.object({
  optionValues: z
    .array(z.string())
    .describe("Array of option labels to select. Each label must be returned by the lookup_options tool."),
});

export const selectOptionCreatableDescription =
  "Set a creatable dropdown to an existing option. If you need to create a new option that does not exist, use the create_option_{id} tool instead (match the select's id from read_current_page)." +
  selectEnabledOptionReminder;

export const selectOptionCreatableParameters = z.object({
  optionValue: z
    .string()
    .describe(
      "The value of the option to select. Must correspond to an option in the dropdown from read_current_page. You MUST read the page content before using this tool to select a valid option."
    ),
});

export const selectOptionsCreatableDescription =
  "Set a creatable multi-select dropdown to one or more existing options. If you need to create a new option that does not exist, use the create_option_{id} tool instead (match the select's id from read_current_page)." +
  selectEnabledOptionReminder;

export const selectOptionsCreatableParameters = z.object({
  optionValues: z
    .array(z.string())
    .describe("Array of option labels to select. Each must correspond to an existing option in the dropdown."),
});

export const selectOptionAsyncCreatableDescription =
  "Set an async creatable dropdown to an existing option. Use the lookup_options tool first to find available options. If you need to create a new option that does not exist, use the create_option_{id} tool instead (match the select's id from read_current_page)." +
  selectEnabledOptionReminder;

export const selectOptionAsyncCreatableParameters = z.object({
  optionValue: z
    .string()
    .describe("The label of the option to select. Must be one of the options returned by the lookup_options tool."),
});

export const selectOptionsAsyncCreatableDescription =
  "Set an async creatable multi-select dropdown to one or more existing options. Use the lookup_options tool first to find available options. If you need to create a new option that does not exist, use the create_option_{id} tool instead (match the select's id from read_current_page)." +
  selectEnabledOptionReminder;

export const selectOptionsAsyncCreatableParameters = z.object({
  optionValues: z
    .array(z.string())
    .describe(
      "Array of option labels to select. Each must be one of the options returned by the lookup_options tool."
    ),
});

export const selectSegmentedControlOptionDescription =
  "Select an option in a segmented control. Read the page content first to find valid option values from the target <select> element." +
  selectEnabledOptionReminder;

export const selectSegmentedControlOptionParameters = z.object({
  optionId: z
    .string()
    .describe(
      "The optionId of an enabled option to select. Must exactly match the value attribute of a non-disabled <option> in the target segmented control <select>. Read the page content to find valid values."
    ),
});

/* ---------------------------- Tabs and stepping ---------------------------- */

export const selectTabDescription =
  "Switch to a tab on the current page. Identify valid tab values from read_current_page before calling." +
  noParallelToolCallsReminder +
  selectEnabledOptionReminder;

export const selectTabParameters = z.object({
  tabId: z
    .string()
    .describe(
      "The value of an enabled tab to switch to. Must correspond to the 'value' attribute of a non-disabled <option> in the <select>... You MUST read the page content before using this tool to select a valid tab."
    ),
});

export const navigateToStepDescription =
  "Navigate to a step in the progress tracker." + noParallelToolCallsReminder + selectEnabledOptionReminder;

export const navigateToStepParameters = z.object({
  stepIndex: z
    .number()
    .int()
    .min(0)
    .describe(
      "The zero-based index of an enabled step to navigate to. Must correspond to the 'value' attribute of an <option> in the target progress tracker <select>. You MUST read the page content before using this tool to select a valid step."
    ),
});

/* -------------------------------- Paging --------------------------------- */

/**
 * Shared by every paginator registering `go_to_page`: `BrazePagination`, `BrazePaginationFooter`
 * and any bespoke pager adopting `useOperatorPagingTools`. The paginator's bounds are on its
 * scraped `<nav>`: `data-current-page` and `data-page-count`, with the `id` to pass as
 * `toolEntityId`.
 */
export const goToPageDescription =
  "Turn a paginated list on the current page to a page, 1-indexed. Read the target <nav> from read_current_page " +
  "first: its id is the toolEntityId and its data-page-count is the last valid page. Only one page of the list " +
  "is visible at a time, so this is how you reach the rest of it." +
  noParallelToolCallsReminder;

export const goToPageParameters = z.object({
  page: z
    .number()
    .int()
    .positive()
    .describe("The page to turn to, 1-indexed, at most the <nav>'s data-page-count."),
});

export const setPageSizeDescription =
  "Change how many rows a paginated list shows per page. Choose one of the values in the target <nav>'s " +
  "data-allowed-page-sizes from read_current_page; its id is the toolEntityId. Most lists return to page 1 when " +
  "their page size changes." +
  noParallelToolCallsReminder;

export const setPageSizeParameters = z.object({
  pageSize: z
    .number()
    .int()
    .positive()
    .describe("The rows to show per page, one of the values in the <nav>'s data-allowed-page-sizes."),
});

/* -------------------------- Checkboxes and tiles --------------------------- */

export const toggleCheckboxDescription =
  'Set a checkbox on or off. Matches the target checkbox in read_current_page. Prefer {"to_checked":true} or {"to_checked":false} so the change is idempotent; pass null for to_checked only when you intentionally want to flip the current state.';

export const toggleCheckboxParameters = z.object({
  to_checked: z
    .boolean()
    .nullable()
    .describe(
      "Set true to turn this on, false to turn it off. Pass null only when you intentionally want the opposite of the current state (toggle)."
    ),
});

export const toggleCheckboxInGroupDescription =
  'Set a checkbox on or off in a checkbox group. Target a checkbox by its visible label from read_current_page. Prefer {"to_checked":true} or {"to_checked":false} so the change is idempotent; pass null for to_checked only when you intentionally want to flip the current state.' +
  checkboxGroupEnabledReminder;

export const toggleCheckboxInGroupParameters = z.object({
  checkbox_label: z
    .string()
    .describe(
      "The visible label of an enabled checkbox to toggle. Must correspond to one of the enabled checkbox labels rendered inside the target fieldset selected by toolEntityId."
    ),
  to_checked: z
    .boolean()
    .nullable()
    .describe(
      "Set true to turn this on, false to turn it off. Pass null only when you intentionally want the opposite of the current state (toggle)."
    ),
});

export const selectTileDescription = "Select a tile in the tile group";

export const selectTileParameters = z.object({
  tileValue: z
    .string()
    .describe(
      "The value of the tile to select. Must correspond to a tile in the target fieldset from read_current_page. You MUST read the page content before using this tool to select a valid tile."
    ),
});

export const selectTilesDescription =
  "Select tiles in the checkbox tile group. This is a multi-select control - you can select or deselect multiple tiles in a single tool call by providing an array of tile values. The provided array will replace the current selection entirely.";

export const selectTilesParameters = z.object({
  tileValues: z
    .array(z.string())
    .describe(
      "An array of tile values to select. Each value must correspond to a tile in the target fieldset from read_current_page. Pass an empty array to deselect all tiles. You MUST read the page content before using this tool to select valid tiles."
    ),
});

/* ------------------------------ Dates and time ----------------------------- */

/** Shared by `BrazeDateField` and `BrazeDatePickerSingle`. */
export const updateDateDescription =
  "Set the matching date field on the current page. Identify the target from read_current_page.";

export const updateDateParameters = z.object({
  date: z.string().describe("The date in YYYY-MM-DD format"),
});

export const updateDateRangeDescription =
  "Set the matching date range field on the current page. Identify the target from read_current_page.";

export const updateDateRangeParameters = z.object({
  startDate: z.string().describe("The start date in YYYY-MM-DD format"),
  endDate: z.string().describe("The end date in YYYY-MM-DD format"),
});

export const selectDateRangeShortcutDescription =
  "Select an enabled predefined shortcut for the matching date range field. Identify the target and valid shortcut labels or keys from read_current_page before calling." +
  selectEnabledOptionReminder;

export const selectDateRangeShortcutParameters = z.object({
  shortcut: z.string().describe("The visible label or key of the date range shortcut to select"),
});

export const setTimeDescription =
  "Set the matching time picker on the current page. Identify the target from read_current_page.";

export const setTimeParameters = z.object({
  hour: z.number().min(0).max(23).describe("The hour to set (0-23, in 24-hour format)"),
  minute: z.number().min(0).max(59).describe("The minute to set (0-59)"),
});

/* -------------------------------- Clipboard -------------------------------- */

/** Shared by `BrazeCopyField`, `BrazeCopyButton` and `BrazeMultilineCopyField`. */
export const copyToClipboardDescription = "Copy a field value to the clipboard";

/* --------------------------- Tables and images ----------------------------- */

export const getDataTableSortingDescription = "List this table's sortable columns and its current sort, if any.";

export const getDataTableSortingParameters = z.object({});

export const setDataTableSortingDescription =
  "Sort this table by a column, replacing any current sort. " +
  "<system-prompt-reminder>Call get_data_table_sorting first to see the exact, sortable column labels." +
  "</system-prompt-reminder>";

export const setDataTableSortingParameters = z.object({
  columnLabel: z.string().describe("The sortable column's visible label."),
  direction: z.enum(["asc", "desc"]).describe("Ascending or descending."),
});

export const updateImageDescription =
  "Sets the image from a direct image URL — e.g. a media library asset URL (see search_media_library) or a generated image URL (see image_generation). Pass image_url: null to remove the current image.";

export const updateImageParameters = z.object({
  image_url: z.string().nullable().describe("Direct URL of the image to use, or null to remove the current image."),
});
